import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { FileReviewAnchor, FileReviewPanelPlacement } from '@jarvis/platform-file-review';
import {
  readEditorAutoSavePrefs,
  EDITOR_AUTOSAVE_PREFS_CHANGED
} from '../editorPreferences/editorAutoSavePrefs';
import type { ForceReloadOptions } from '../realtime/documentCollaborationTypes';
import type {
  AutoSaveBridgeStatus,
  RichEditBridgeMessage,
  RichEditHostMessage,
  RichEditSelectionState
} from './richEditBridgeTypes';

function isBridgeMessage(data: unknown): data is RichEditBridgeMessage {
  return Boolean(data && typeof data === 'object' && 'type' in data);
}

export interface AutoSaveBridgeState {
  status: AutoSaveBridgeStatus;
  lastSavedAt: number | null;
  reason: string | null;
}

export interface AutoSaveRequestResult {
  ok: boolean;
  reason: string | null;
}

const DIRTY_STATE_TIMEOUT_MS = 1500;
const SAVE_REQUEST_TIMEOUT_MS = 70000;

export interface UseRichEditHostBridgeOptions {
  onNavigationFailed?: () => void;
  onOpenFailed?: (message?: string | null) => void;
  onRequestCommentOnSelection?: () => void;
  onRequestToggleTextLinks?: () => void;
  onRequestToggleDiscussion?: () => void;
  onRequestShare?: () => void;
  onRequestOpen?: (source: 'pc' | 'storage') => void;
  onRequestSetDiscussionPlacement?: (placement: FileReviewPanelPlacement) => void;
}

export function useRichEditHostBridge(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  enabled: boolean,
  options?: UseRichEditHostBridgeOptions
) {
  const [ready, setReady] = useState(false);
  const [editorSessionId, setEditorSessionId] = useState<string | null>(null);
  const [selection, setSelection] = useState<RichEditSelectionState | null>(null);
  const [autoSave, setAutoSave] = useState<AutoSaveBridgeState>({
    status: 'idle',
    lastSavedAt: null,
    reason: null
  });
  const dirtyResolverRef = useRef<{ resolve: (dirty: boolean) => void; timer: number } | null>(null);
  const saveResolverRef = useRef<{
    requestId: string;
    resolve: (result: AutoSaveRequestResult) => void;
    timer: number;
  } | null>(null);
  const iframeOriginRef = useRef<string | null>(null);
  const onNavigationFailedRef = useRef(options?.onNavigationFailed);
  const onOpenFailedRef = useRef(options?.onOpenFailed);
  const onRequestCommentRef = useRef(options?.onRequestCommentOnSelection);
  const onRequestToggleTextLinksRef = useRef(options?.onRequestToggleTextLinks);
  const onRequestToggleDiscussionRef = useRef(options?.onRequestToggleDiscussion);
  const onRequestShareRef = useRef(options?.onRequestShare);
  const onRequestOpenRef = useRef(options?.onRequestOpen);
  const onRequestSetDiscussionPlacementRef = useRef(options?.onRequestSetDiscussionPlacement);
  onNavigationFailedRef.current = options?.onNavigationFailed;
  onOpenFailedRef.current = options?.onOpenFailed;
  onRequestCommentRef.current = options?.onRequestCommentOnSelection;
  onRequestToggleTextLinksRef.current = options?.onRequestToggleTextLinks;
  onRequestToggleDiscussionRef.current = options?.onRequestToggleDiscussion;
  onRequestShareRef.current = options?.onRequestShare;
  onRequestOpenRef.current = options?.onRequestOpen;
  onRequestSetDiscussionPlacementRef.current = options?.onRequestSetDiscussionPlacement;

  const postToHost = useCallback(
    (message: RichEditHostMessage) => {
      const iframe = iframeRef.current;
      const win = iframe?.contentWindow;
      if (!win) return;
      let targetOrigin = window.location.origin;
      try {
        if (iframe.src) targetOrigin = new URL(iframe.src, window.location.href).origin;
      } catch {
        /* keep parent origin */
      }
      win.postMessage(message, targetOrigin);
    },
    [iframeRef]
  );

  const setDiscussionState = useCallback(
    (on: boolean) => {
      postToHost({ type: 'jarvis-richedit:discussion-state', on });
    },
    [postToHost]
  );

  const setTextLinksState = useCallback(
    (on: boolean) => {
      postToHost({ type: 'jarvis-richedit:text-links-state', on });
    },
    [postToHost]
  );

  const setDiscussionPlacementState = useCallback(
    (placement: FileReviewPanelPlacement) => {
      postToHost({ type: 'jarvis-richedit:discussion-placement-state', placement });
    },
    [postToHost]
  );

  const requestAutoSaveNow = useCallback((): Promise<AutoSaveRequestResult> => {
    const prior = saveResolverRef.current;
    if (prior) {
      window.clearTimeout(prior.timer);
      prior.resolve({ ok: false, reason: 'superseded' });
      saveResolverRef.current = null;
    }
    const requestId =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return new Promise<AutoSaveRequestResult>((resolve) => {
      const timer = window.setTimeout(() => {
        if (saveResolverRef.current?.requestId === requestId) saveResolverRef.current = null;
        resolve({ ok: false, reason: 'timeout' });
      }, SAVE_REQUEST_TIMEOUT_MS);
      saveResolverRef.current = { requestId, resolve, timer };
      postToHost({ type: 'jarvis-richedit:request-auto-save-now', requestId });
    });
  }, [postToHost]);

  const setPeerEditLocked = useCallback(
    (locked: boolean) => {
      postToHost({ type: 'jarvis-richedit:set-peer-edit-locked', locked });
    },
    [postToHost]
  );

  const requestForceReload = useCallback((options?: ForceReloadOptions) => {
    postToHost({
      type: 'jarvis-richedit:request-force-reload',
      overlay: options?.overlay === true
    });
  }, [postToHost]);

  const applyCollaborationSaveTime = useCallback((savedAtUtc: string) => {
    const ms = Date.parse(savedAtUtc);
    const lastSavedAt = Number.isFinite(ms) ? ms : Date.now();
    setAutoSave({ status: 'saved', lastSavedAt, reason: null });
  }, []);

  const requestDirtyState = useCallback((): Promise<boolean> => {
    const prior = dirtyResolverRef.current;
    if (prior) {
      window.clearTimeout(prior.timer);
      prior.resolve(true);
      dirtyResolverRef.current = null;
    }
    return new Promise<boolean>((resolve) => {
      const timer = window.setTimeout(() => {
        dirtyResolverRef.current = null;
        resolve(true);
      }, DIRTY_STATE_TIMEOUT_MS);
      dirtyResolverRef.current = { resolve, timer };
      postToHost({ type: 'jarvis-richedit:request-dirty-state' });
    });
  }, [postToHost]);

  const navigateToAnchor = useCallback(
    (anchor: FileReviewAnchor) => {
      if (anchor.kind !== 'word' || !anchor.wordRange) return;
      postToHost({
        type: 'jarvis-richedit:navigate-anchor',
        payload: {
          anchorId: anchor.id,
          range: anchor.wordRange
        }
      });
    },
    [postToHost]
  );

  useEffect(() => {
    if (!enabled) {
      setReady(false);
      setEditorSessionId(null);
      setSelection(null);
    }
  }, [enabled]);

  useEffect(
    () => () => {
      const dirty = dirtyResolverRef.current;
      if (dirty) {
        window.clearTimeout(dirty.timer);
        dirty.resolve(true);
        dirtyResolverRef.current = null;
      }
      const save = saveResolverRef.current;
      if (save) {
        window.clearTimeout(save.timer);
        save.resolve({ ok: false, reason: 'unmounted' });
        saveResolverRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (!enabled) return;

    function onMessage(event: MessageEvent) {
      const iframeOrigin = iframeOriginRef.current;
      const originOk =
        event.origin === window.location.origin ||
        (iframeOrigin != null && event.origin === iframeOrigin);
      if (!originOk) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!isBridgeMessage(event.data)) return;

      switch (event.data.type) {
        case 'jarvis-richedit:ready':
          if (event.data.hostOrigin) iframeOriginRef.current = event.data.hostOrigin;
          setEditorSessionId(event.data.sessionId?.trim() || null);
          setReady(true);
          break;
        case 'jarvis-richedit:open-failed':
          onOpenFailedRef.current?.(event.data.message ?? null);
          break;
        case 'jarvis-richedit:selection-changed':
          setSelection(event.data.selection);
          break;
        case 'jarvis-richedit:request-comment-on-selection':
          onRequestCommentRef.current?.();
          break;
        case 'jarvis-richedit:request-toggle-text-links':
          onRequestToggleTextLinksRef.current?.();
          break;
        case 'jarvis-richedit:request-toggle-discussion':
          onRequestToggleDiscussionRef.current?.();
          break;
        case 'jarvis-richedit:request-share':
          onRequestShareRef.current?.();
          break;
        case 'jarvis-richedit:request-open': {
          const source = event.data.source === 'storage' ? 'storage' : 'pc';
          onRequestOpenRef.current?.(source);
          break;
        }
        case 'jarvis-richedit:request-set-discussion-placement':
          onRequestSetDiscussionPlacementRef.current?.(event.data.placement);
          break;
        case 'jarvis-richedit:navigation-complete':
          if (!event.data.ok) onNavigationFailedRef.current?.();
          break;
        case 'jarvis-richedit:dirty-state': {
          const pending = dirtyResolverRef.current;
          if (pending) {
            window.clearTimeout(pending.timer);
            dirtyResolverRef.current = null;
            pending.resolve(Boolean(event.data.dirty));
          }
          break;
        }
        case 'jarvis-richedit:auto-save-state':
          setAutoSave({
            status: event.data.status,
            lastSavedAt: event.data.lastSavedAtUtc ?? null,
            reason: event.data.reason ?? null
          });
          break;
        case 'jarvis-richedit:auto-save-complete': {
          const pending = saveResolverRef.current;
          if (pending && pending.requestId === event.data.requestId) {
            window.clearTimeout(pending.timer);
            saveResolverRef.current = null;
            pending.resolve({
              ok: Boolean(event.data.ok),
              reason: event.data.reason ?? null
            });
          }
          break;
        }
        default:
          break;
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [enabled, iframeRef]);

  const pushAutoSaveConfig = useCallback(() => {
    const prefs = readEditorAutoSavePrefs();
    postToHost({
      type: 'jarvis-richedit:set-autosave-config',
      enabled: prefs.enabled,
      intervalSeconds: prefs.intervalSeconds
    });
  }, [postToHost]);

  useEffect(() => {
    if (!enabled || !ready) return;
    pushAutoSaveConfig();
  }, [enabled, ready, pushAutoSaveConfig]);

  useEffect(() => {
    if (!enabled || !ready) return;
    const onPrefsChanged = () => pushAutoSaveConfig();
    window.addEventListener(EDITOR_AUTOSAVE_PREFS_CHANGED, onPrefsChanged);
    return () => window.removeEventListener(EDITOR_AUTOSAVE_PREFS_CHANGED, onPrefsChanged);
  }, [enabled, ready, pushAutoSaveConfig]);

  return {
    ready,
    editorSessionId,
    selection,
    autoSave,
    navigateToAnchor,
    requestAutoSaveNow,
    requestForceReload,
    applyCollaborationSaveTime,
    requestDirtyState,
    postToHost,
    setDiscussionState,
    setTextLinksState,
    setDiscussionPlacementState,
    setPeerEditLocked
  };
}
